import { calloutLine, calloutWhen, isCallout } from './liveClock.js'
import { periodName } from './model.js'
import { sideOf } from './shotModel.js'

// The design's "Flow": the win-probability curve and the list of swings. P6-330b (GN-3b).
//
// Pure functions over the game blob's `win_probability` (`[{seq, period, clock, home}, ...]`, ESPN's curve, the HOME
// team's chance as 0 to 1) and `callouts` (the producer's newest-first notable moments, at most five). Nothing here
// reads a clock or a zone.
//
// ═══════════════════════════════════════════════════════════════════════════
// THE CURVE IS DRAWN AGAINST ITS POSITION, NOT AGAINST THE GAME CLOCK
// ═══════════════════════════════════════════════════════════════════════════
//
// The producer's contract is explicit (`utils/hoops_projection.py`, "THE WIN PROBABILITY"): the points are in ESPN's
// own order, `seq` is what strictly increases, and THE CLOCK IS NOT: it steps backwards twice in the opener's 595
// points, both times in the first period, because ESPN files a late play out of clock order. A curve drawn against
// game time doubles back on itself at each of those, so the x axis here is the point's place in the list. The
// period names under the plot sit where each period's first point is, which is where the period began on the curve.
//
// ⚠️ A CURVE IS DRAWN ONLY WHEN IT HAS A LINE TO DRAW. A game with no points (every preseason game: the key is
// ABSENT, and that is correct) and a game with one are the same answer here, `null`: no flat line at 50 percent, no
// empty plot, no heading with nothing under it. A point that is not a chance (not a number, or outside 0 to 1) is
// left out rather than clamped: a clamped 1.2 is a claim ESPN did not make.

const isChance = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1

/** The points of a curve that are chances, in the order they came. */
export function chancePoints(points) {
  return (Array.isArray(points) ? points : []).filter((point) => point !== null && typeof point === 'object' && isChance(point.home))
}

/** The HOME team's chance at the curve's last point, or `null` when the curve has none. */
export function lastChance(points) {
  const kept = chancePoints(points)
  return kept.length === 0 ? null : kept[kept.length - 1].home
}

/** The plot's own box: 300 wide, 100 tall, home at the top. */
export const CURVE_BOX = Object.freeze({ width: 300, height: 100 })

const one = (value) => value.toFixed(1)
const percent = (chance) => Math.round(chance * 100)

/**
 * The curve as paths, and where each period begins.
 *
 * `line` runs through every point; `area` is the line closed down to the 50 percent line, so the plot fills in the
 * home team's colour above it and the away team's below; `starts` are the periods, each with the percentage of the
 * plot's width its first point sits at. Home is at the TOP (`up = HOME`), so a home chance of 1 is `y` 0.
 */
export function curveModel(points) {
  const kept = chancePoints(points)
  if (kept.length < 2) return null
  const last = kept.length - 1
  const x = (index) => (index / last) * CURVE_BOX.width
  const y = (chance) => (1 - chance) * CURVE_BOX.height
  const line = `M ${kept.map((point, index) => `${one(x(index))} ${one(y(point.home))}`).join(' L ')}`
  const area = `${line} L ${one(x(last))} 50 L 0 50 Z`

  const starts = []
  let previous = null
  kept.forEach((point, index) => {
    if (point.period === previous) return
    previous = point.period
    const label = periodName(point.period)
    if (label !== null) starts.push({ period: point.period, label, x: x(index), left: Number(((index / last) * 100).toFixed(2)) })
  })

  const chances = kept.map((point) => point.home)
  return {
    count: kept.length,
    line,
    area,
    starts,
    first: kept[0].home,
    last: kept[last].home,
    high: Math.max(...chances),
    low: Math.min(...chances),
  }
}

/**
 * The curve's text alternative: it is a picture of a series, so it says where the series started, where it is, and
 * which way is up. `home` is the home team's tricode.
 */
export function curveAria(curve, home) {
  return `Win probability by play, up is ${home}: ${home} ${percent(curve.first)} percent at the start, ${percent(curve.last)} percent at the last play, from ${percent(curve.low)} to ${percent(curve.high)}.`
}

/** The most swings the list draws: the design's own `slice(-6)`. The producer holds five. */
export const SWING_LIMIT = 6

/**
 * The swing list, newest first, from the game blob's `callouts`.
 *
 * ⚠️ WHAT FEEDS IT. The design's list was built from the fifty plays the page held (every run of six points or more
 * and every lead change in them). The game blob carries the producer's own `callouts` instead: the latest five
 * notable moments of the WHOLE game (a lead change, a run of eight or more, a new game-high lead from ten), newest
 * first, each `{kind, period, clock, team, text}`. The page draws those, in the order given, with the team's tick
 * and the period and clock they were last true on, and it does not recount anything: the producer's rules are the
 * producer's.
 */
export function swingRows(game) {
  const list = Array.isArray(game?.callouts) ? game.callouts : []
  const seen = new Map()
  const rows = []
  for (const callout of list) {
    if (!isCallout(callout)) continue
    const base = [callout.kind, callout.period, callout.clock, callout.team, callout.text].join('|')
    const n = (seen.get(base) ?? 0) + 1
    seen.set(base, n)
    rows.push({
      key: `${base}|${n}`,
      line: calloutLine(callout),
      when: calloutWhen(callout),
      kind: callout.kind,
      side: sideOf(game, callout.team),
    })
  }
  return rows.slice(0, SWING_LIMIT)
}
